import { create } from "zustand";
import { httpService } from "../../../base/services/httpService.service";
import {
   debounce,
   addQuestion,
   deleteQuestion,
   addInnerSection,
   deleteSection,
   addSection,
   addFirstSection,
   duplicateQuestion,
   updateQuestionHint,
   updateQuestionLabel,
   updateQuestionType,
   updateQuestionChoices,
   updateSecondQuestionChoices,
   updateRangeValues,
   updateIsRequiredStatus,
   addEmojiStatus,
   addImageToSurveyData,
   removeImageFromSurveyData,
   updateMaxChoices,
   updateQuestionCalculation,
   updateQuestionDefault,
   normalizeItemOrder,
   renumberItems
} from "./helpers";
import { Constants } from "../../../utils/constants";
import { loadDraft } from "../../../storage/db";
import { InputType } from "../../../utils/enums";
import {
   LEGACY_PREVIEW_PATH,
   buildPreviewParams,
   fetchFlatPreviewData,
   isFlatPreviewEnabled
} from "./previewData/previewDataApi";

export const useSurveyStore = create((set) => ({
   isLoading: false,
   isSurveyLoading: false,
   surveyAlldata: [],
   surveyData: [],
   surveyBaseLanguage: "ecfb845a-86f0-476c-a4df-f101d35a96f9",
   arabicLanguageID: Constants.ARABIC_LANGUAGE_ID,
   surveyBaseLanguageName: "",

   fetchSurveyById: (payload, loading = true) => {
      set({ isSurveyLoading: loading });

      httpService.post(
         "Survey/GetById",
         {
            SurveyId: payload.surveyId,
            languageId: payload.languageId
         },
         (res) => {
            const language =
               res?.data.surveyLanguageMaps?.find((lang) => lang.isBaseLanguage) || null;
            set({
               surveyAlldata: res?.data,
               surveyData: res?.data?.surveyItems,
               isSurveyLoading: false,
               surveyBaseLanguage: language.languageId, // You can use the correct value here
               surveyBaseLanguageName: language.name
            });
         }
      );
   },
   fetchSurveyByyId: (payload, loading = true) => {
      set({ isSurveyLoading: loading });

      httpService.get(
         `/Survey/GetPreviewData?surveyId=${payload.surveyId}&languageId=${payload.languageId}`,
         // {
         //    SurveyId: payload.surveyId,
         //    languageId: payload.languageId
         // },
         (res) => {
            const language =
               res?.data.surveyLanguageMaps?.find((lang) => lang.isBaseLanguage) || null;
            set({
               surveyAlldata: res?.data,
               surveyData: res?.data?.surveyItems,
               isSurveyLoading: false,
               surveyBaseLanguage: language.languageId, // You can use the correct value here
               surveyBaseLanguageName: language.name
            });
         }
      );
   },
   setSurveySubmissionData: (key, value) => {
      set((state) => ({
         surveySubmissionData: {
            ...state.surveySubmissionData,
            [key]: value
         }
      }));
   },

   // Resolves with the survey details, and rejects with the request's error so
   // the caller can tell a survey that isn't there (404) from a failed read.
   fetchSurveyDetailsById: (surveyId, loading = true) => {
      set({ isSurveyLoading: loading });

      return httpService.get(
         `Survey/GetSurveyDetails?surveyId=${surveyId}`,
         (res) => {
            const surveyAllData = {
               ...res.data,
               languageId: res.data?.surveyLanguageMaps?.find((language) => language.isBaseLanguage)
                  ?.languageId
            };
            set({
               surveyAlldata: surveyAllData,
               isSurveyLoading: false
            });
         },
         () => set({ isSurveyLoading: false })
      );
   }
}));

export const useSurveyInputStore = create((set) => ({
   inputs: {},
   getErrors: (renderedElementIds, inputs) => {
      const surveyQuestions = editDataStore.getState().surveyQuestions;
      const requiredQuestionIds = new Set(
         surveyQuestions.filter((question) => question.isRequired).map((question) => question.id)
      );

      // A rendered element's id is the key its input is stored under: the bare question id
      // outside a repeat, and "<survey>/<group>__repeat/<questionId>/<n>" for one instance
      // inside one. Walking the rendered ids rather than the question list is what brings repeat
      // instances into the check - looking each question up by its bare id found nothing for a
      // repeated one, so a required question left empty inside a repeat group raised no error -
      // and it keeps every instance of the same question separate. The keys handed back are
      // `data-id` values, which is what the caller scrolls the first error into view by.
      return renderedElementIds.filter((elementId) => {
         const questionId = /\/\d+$/.test(elementId) ? elementId.split("/").at(-2) : elementId;

         if (!requiredQuestionIds.has(questionId)) {
            return false;
         }

         const input = inputs[elementId];

         if (!input) {
            return true;
         }

         const answerDetailKey = Object.keys(input.answerDetail)[0];
         const answerDetail = input.answerDetail[answerDetailKey];

         return Boolean(
            answerDetail &&
               (answerDetail === "" ||
                  answerDetail.length === 0 ||
                  (typeof answerDetail === "string" && answerDetail.trim() === ""))
         );
      });
   },
   setInputs: (e) => {
      // type e = {
      //    name: string,
      //    value: {
      //        surveyId: string,
      //        userId: string,
      //        questionId: string,
      //        questionType: 40,
      //        name: string
      //        skipLogicAnswer: any,
      //        answerDetail: {
      //            [key:string]: any
      //        }
      //    }
      // }
      if (e) {
         set((state) => {
            // name id the question id - uuid
            const { name, value } = e?.target;
            const updatedInputs = { ...state.inputs };

            if (value !== "") {
               updatedInputs[name] = value;
            } else {
               delete updatedInputs[name];
            }

            return { inputs: updatedInputs };
         });
      } else {
         set(() => {
            return { inputs: {} };
         });
      }
   },
   setDraftSurvey: async (surveyId, draftId) => {
      //TODO: try catch
      const draftData = await loadDraft(surveyId, draftId);
      set({ inputs: draftData.formData });
   },
   resetInputs: () => {
      set(({ inputs }) => {
         const surveyQuestions = editDataStore.getState().surveyQuestions;

         // The one thing an emptied form keeps: an answer that is the question's own default.
         // The component writes that once, when it mounts, and a reset does not remount it - so
         // dropping it would leave the default on screen with nothing behind it.
         const keepsItsDefault = new Set(
            surveyQuestions.filter((question) => question.default !== null).map(({ id }) => id)
         );

         // Walking the store's own keys rather than the question list is what makes the reset
         // reach everything an entry left behind. Only a question outside a repeat group is keyed
         // by its bare id: an instance inside one is keyed "<survey>/<group>__repeat/<id>/<n>" and
         // a repeat section's counter by the section id, so neither matched a question id and both
         // survived - the next draft opened holding the previous one's children, and in kiosk mode
         // one respondent's answers carried into the next. Repeat instances are unmounted and
         // rebuilt whenever the group's count changes, defaults included, so none of them is worth
         // keeping either.
         return {
            inputs: Object.fromEntries(
               Object.entries(inputs).filter(
                  ([key]) => !/\/\d+$/.test(key) && keepsItsDefault.has(key)
               )
            )
         };
      });
   }
}));

// Identifies the latest fetchSurveyById request so responses of superseded
// requests (e.g. a slow fetch failing after a newer one succeeded) are ignored
let fetchSurveyByIdRequestId = 0;

const ITEM_LABEL = {
   SECTION: "Section",
   QUESTION: "Question"
};

const getQuestionsRecursively = (items) => {
   return items.flatMap((item) => {
      if (item.itemLabel === ITEM_LABEL.SECTION) {
         return getQuestionsRecursively(item.innerItems);
      } else {
         return item.surveyItem;
      }
   });
};

function assignXpathRecursively(items, parentXpath) {
   items.forEach((item) => {
      item.xpath = `${parentXpath}/${item.surveyItem.name}${
         item.itemLabel === ITEM_LABEL.SECTION && item.surveyItem.repeat_count ? "__repeat" : ""
      }`;
      if (item.innerItems) {
         assignXpathRecursively(item.innerItems, item.xpath);
      }
   });
}

// Post-processing of a preview payload, shared by both endpoints: Survey/GetPreviewData returns
// this shape directly and Survey/GetPreviewDataFlat is adapted into it, so the store ends up
// holding exactly the same state either way.
function toPreviewState(data) {
   // Sorted into render order and renumbered 1..n, level by level. The payload lists a level's
   // sections ahead of the questions that precede them and `order` is what says otherwise, so the
   // tabs that sorted and the tabs that read the array straight showed a question sitting outside
   // a section in two different places. Only a section's direct children were renumbered before,
   // which left out the survey's own level - where such a question lives - and nested sections.
   const surveyData = normalizeItemOrder(data?.surveyItems ?? []);

   assignXpathRecursively(surveyData, data?.id);

   const language = data?.surveyLanguageMaps?.find((lang) => lang.isBaseLanguage) || null;

   return {
      surveyDataResponse: JSON.stringify(data),
      isSurveyLoading: false,
      surveyData: surveyData,
      surveyQuestions: getQuestionsRecursively(surveyData),
      surveyAllData: data,
      surveyBaseLanguage: language.languageId,
      surveyBaseLanguageName: language.name,
      toggleLocation: data.toggleLocation
   };
}

function toPreviewErrorState(_error, { loading, surveyId }) {
   // Only clear the store when it doesn't already hold this survey's data,
   // so a previously opened survey is never shown as this one. Failed refetches
   // of an already loaded survey (language switch, background saves) keep the
   // current data so the user can still interact with it and retry
   const hasCurrentSurveyData = editDataStore.getState().surveyAllData?.id === surveyId;

   if (loading && !hasCurrentSurveyData) {
      const errorData = _error?.response?.data || _error;
      return {
         isSurveyLoading: false,
         surveyData: [],
         surveyQuestions: [],
         surveyAllData: {},
         surveyDataResponse: {},
         surveyBaseLanguage: null,
         surveyBaseLanguageName: null,
         toggleLocation: false,
         // The API has no such survey for this account: deleted, another organization's,
         // or a Standard survey opened from a business account.
         surveyFetchNotFound:
            _error?.response?.status === 404 ||
            _error?.statusCode === 404 ||
            errorData?.statusCode === 404,
         surveyFetchError:
            (Array.isArray(errorData?.errors) && errorData.errors.join(" ")) ||
            errorData?.message ||
            errorData?.title ||
            _error?.message ||
            "Something went wrong! Please try again later."
      };
   }

   return { isSurveyLoading: false };
}

export const editDataStore = create((set) => ({
   editData: {
      deletedSections: [],
      deletedQuestions: []
   },
   isSurveyLoading: false,
   surveyData: [],
   surveyQuestions: [],
   surveyAllData: {},
   surveyDataResponse: {},
   surveyFetchError: null,
   surveyFetchNotFound: false,
   // Last `SurveyVersion` an `api/SurveyBuilder` write reported, sent back as `If-Match` so the
   // next write is refused with a 409 rather than quietly overwriting somebody else's edit.
   //
   // It is held with the survey it belongs to because this store is a module-level singleton that
   // outlives the page: opening a second survey must not send it the first one's version. The
   // read endpoints report no version, so it stays null until this session writes something -
   // which only costs the first save after a load its `If-Match`.
   surveyVersion: null,
   surveyVersionSurveyId: null,
   toggleLocation: false,

   setSurveyVersion: (surveyId, surveyVersion) =>
      set({ surveyVersion, surveyVersionSurveyId: surveyId }),

   /**
    * Drops the pristine copy the diff is taken against.
    *
    * Called when the refetch after a save fails. That copy then describes a survey that no longer
    * exists - every operation in the save is already applied server-side - but this store
    * deliberately keeps the loaded survey when a read fails, so the copy would otherwise stay and
    * the next save would send the same operations again (adds mint new ids, so they would
    * duplicate rows, and the version is current, so `If-Match` cannot catch it). With no
    * baseline, `saveSurveyEdits` falls back to the whole-tree `Survey/EditSurvey` write, which a
    * repeat cannot damage.
    */
   clearSurveyBaseline: () => set({ surveyDataResponse: {} }),

   resetEditData: () => {
      set({
         editData: {
            deletedSections: [],
            deletedQuestions: []
         }
      });
   },
   handleDnDChange: (result) => {
      const { destination, source } = result;

      // If dropped outside a valid destination
      if (!destination) return;

      // If dropped in the same position
      if (source.droppableId === destination.droppableId && source.index === destination.index) {
         return;
      }

      // Helper to find and remove the item from the source
      const findAndRemoveItem = (items, droppableId, index) => {
         for (const item of items) {
            if (item.id === droppableId) {
               // Found the target section or question
               return [item.innerItems.splice(index, 1)[0], items];
            }
            if (item.innerItems) {
               const [foundItem] = findAndRemoveItem(item.innerItems, droppableId, index);
               if (foundItem) return [foundItem, items];
            }
         }
         return [null, items];
      };

      // Helper to insert the item into the destination
      const insertItem = (items, droppableId, index, itemToInsert) => {
         for (const item of items) {
            if (item.id === droppableId) {
               // Found the target section or question
               item.innerItems.splice(index, 0, itemToInsert);
               return items;
            }
            if (item.innerItems) {
               const updatedInnerItems = insertItem(
                  item.innerItems,
                  droppableId,
                  index,
                  itemToInsert
               );
               if (updatedInnerItems?.length) {
                  return items;
               }
            }
         }
         return items;
      };

      // Clone the sections to avoid mutating state directly
      const updatedSections = structuredClone(editDataStore.getState().surveyData);

      // Remove the item from the source
      const [movedItem, sectionsWithoutSource] = findAndRemoveItem(
         updatedSections,
         source.droppableId,
         source.index
      );

      if (!movedItem) return; // If no item was found, abort

      // Insert the item into the destination
      const sectionsWithInsertedItem = insertItem(
         sectionsWithoutSource,
         destination.droppableId,
         destination.index,
         movedItem
      );

      renumberItems(sectionsWithInsertedItem);

      set({ surveyData: sectionsWithInsertedItem });
   },
   changeSurveyStatus: (newStatus) => {
      set((state) => ({
         surveyAllData: {
            ...state.surveyAllData,
            surveyStatusId: newStatus
         }
      }));
   },
   // Resolves true when the survey was read, false when the read failed - never rejects, so the
   // callers that ignore the answer stay as they were. A caller that cares is the save: it has to
   // know whether the copy it diffs against was replaced (see `clearSurveyBaseline`).
   fetchSurveyById: (payload, loading = true) => {
      const requestId = ++fetchSurveyByIdRequestId;
      set({ isSurveyLoading: loading, surveyFetchError: null, surveyFetchNotFound: false });

      return httpService
         .get(
            `${LEGACY_PREVIEW_PATH}?${buildPreviewParams(payload).toString()}`,
            (res) => {
               if (requestId !== fetchSurveyByIdRequestId) return;

               set(toPreviewState(res?.data));
            },
            (_error) => {
               if (requestId !== fetchSurveyByIdRequestId) return;

               set(toPreviewErrorState(_error, { loading, surveyId: payload.surveyId }));
            }
         )
         .then(
            () => true,
            () => false
         );
   },
   // Used by the survey renderer/filler only. The survey builder keeps calling fetchSurveyById:
   // the flat endpoint resolves every text into one language, which is all the renderer needs but
   // not enough for the translate panel's per-language editing.
   fetchSurveyPreview: (payload, loading = true) => {
      if (!isFlatPreviewEnabled()) {
         editDataStore.getState().fetchSurveyById(payload, loading);
         return;
      }

      const requestId = ++fetchSurveyByIdRequestId;
      set({ isSurveyLoading: loading, surveyFetchError: null, surveyFetchNotFound: false });

      fetchFlatPreviewData(payload).then(
         (data) => {
            if (requestId !== fetchSurveyByIdRequestId) return;

            set(toPreviewState(data));
         },
         (_error) => {
            if (requestId !== fetchSurveyByIdRequestId) return;

            set(toPreviewErrorState(_error, { loading, surveyId: payload.surveyId }));
         }
      );
   },
   deleteQuestion: (sectionId, questionId) => {
      const updatedSurveyData = [...editDataStore.getState().surveyData];
      const editData = { ...editDataStore.getState().editData };

      if (questionId === InputType.SECTION) {
         deleteSection(updatedSurveyData, sectionId);
         set({
            surveyData: updatedSurveyData,
            editData: { ...editData, deletedSections: [...editData.deletedSections, sectionId] }
         });
      } else {
         deleteQuestion(updatedSurveyData, questionId);
         set({
            surveyData: updatedSurveyData,
            editData: { ...editData, deletedQuestions: [...editData.deletedQuestions, questionId] }
         });
      }
   },
   addQuestion: (payload) => {
      set((state) => {
         const updatedSurveyData = [...state.surveyData];

         if (payload.questionType === 0) {
            addInnerSection(updatedSurveyData, payload);
         } else {
            addQuestion(updatedSurveyData, payload);
         }
         return { surveyData: updatedSurveyData };
      });
   },
   duplicateElement: (sectionId, questionId) => {
      set((state) => {
         const updatedSurveyData = [...state.surveyData];

         duplicateQuestion(updatedSurveyData, questionId);
         return { surveyData: updatedSurveyData };
      });
   },
   addSection: (targetId) => {
      set((state) => {
         const updatedSurveyData = [...state.surveyData];

         if (targetId === 0) {
            addFirstSection(updatedSurveyData);
         } else {
            addSection(updatedSurveyData, targetId);
         }

         return { surveyData: updatedSurveyData };
      });
   },
   changeRequiredStatus: (id, status) => {
      set((state) => {
         const updatedEditData = { ...state.editData };
         if (!updatedEditData[id]) {
            updatedEditData[id] = { isRequired: status };
         } else {
            updatedEditData[id].isRequired = status;
         }
         const updatedSurveyData = updateIsRequiredStatus(state.surveyData, id, status);

         return { editData: updatedEditData, surveyData: updatedSurveyData };
      });
   },
   addEmoji: (id, emojiStatus) => {
      set((state) => {
         const updatedEditData = { ...state.editData };
         if (!updatedEditData[id]) {
            updatedEditData[id] = { hasEmoji: emojiStatus };
         } else {
            updatedEditData[id].hasEmoji = emojiStatus;
         }
         const updatedSurveyData = addEmojiStatus(state.surveyData, id, emojiStatus);

         return { editData: updatedEditData, surveyData: updatedSurveyData };
      });
   },
   addImage: (id, image) => {
      set((state) => {
         const updatedEditData = { ...state.editData };
         if (!updatedEditData[id]) {
            updatedEditData[id] = { image: image };
         } else {
            updatedEditData[id].image = image;
         }
         // It updates the surveyData structure by calling a utility function addImageToSurveyData,
         // which places the File object into the appropriate question object within the survey structure.
         const updatedSurveyData = addImageToSurveyData(state.surveyData, id, image);

         return { editData: updatedEditData, surveyData: updatedSurveyData };
      });
   },
   // remove image from store so when saving the changes, we don't send the image on backend, since we only applied it
   removeImage: (questionId) =>
      set((state) => {
         const updatedEditData = { ...state.editData };
         if (updatedEditData[questionId]) {
            delete updatedEditData[questionId].image;
         }

         const updatedSurveyData = removeImageFromSurveyData(state.surveyData, questionId);

         return { editData: updatedEditData, surveyData: updatedSurveyData };
      }),
   // From here down, `sectionId` is only part of the call signature the editors already use: the
   // helpers find an item by its own id now, wherever it sits, because the `sectionId` an editor
   // passes is the one the read put on the question and is not always the parent it actually has
   // (see `findItemById`).
   setQuestionCalculation: (sectionId, id, calculation) => {
      set((state) => {
         const updatedEditData = { ...state.editData };
         if (!updatedEditData[id]) {
            updatedEditData[id] = { calculation: calculation };
         } else {
            updatedEditData[id].calculation = calculation;
         }
         const updatedSurveyData = updateQuestionCalculation(state.surveyData, id, calculation);

         return { editData: updatedEditData, surveyData: updatedSurveyData };
      });
   },
   setQuestionDefault: (sectionId, id, defaultValue) => {
      set((state) => {
         const updatedEditData = { ...state.editData };
         if (!updatedEditData[id]) {
            updatedEditData[id] = { default: defaultValue };
         } else {
            updatedEditData[id].default = defaultValue;
         }
         const updatedSurveyData = updateQuestionDefault(state.surveyData, id, defaultValue);

         return { editData: updatedEditData, surveyData: updatedSurveyData };
      });
   },
   setQuestionTitle: (sectionId, id, text) => {
      set((state) => {
         const updatedEditData = { ...state.editData };
         if (!updatedEditData[id]) {
            updatedEditData[id] = { title: text };
         } else {
            updatedEditData[id].title = text;
         }
         const updatedSurveyData = updateQuestionLabel(state.surveyData, id, text);

         return { editData: updatedEditData, surveyData: updatedSurveyData };
      });
   },
   setQuestionHint: (sectionId, id, text) => {
      set((state) => {
         const updatedEditData = { ...state.editData };

         if (!updatedEditData[id]) {
            updatedEditData[id] = { hint: text };
         } else {
            updatedEditData[id].hint = text;
         }

         const updatedSurveyData = updateQuestionHint(state.surveyData, id, text);
         return { editData: updatedEditData, surveyData: updatedSurveyData };
      });
   },
   setQuestionType: (sectionId, id, type) => {
      set((state) => {
         const updatedEditData = { ...state.editData };
         if (!updatedEditData[id]) {
            updatedEditData[id] = { type: type };
         } else {
            updatedEditData[id].type = type;
         }
         const updatedSurveyData = updateQuestionType(state.surveyData, id, type);
         return { editData: updatedEditData, surveyData: updatedSurveyData };
      });
   },
   setQuestionChoices: (sectionId, id, choices) => {
      set((state) => {
         const updatedEditData = { ...state.editData };
         if (!updatedEditData[id]) {
            updatedEditData[id] = {
               choices: choices
            };
         } else {
            updatedEditData[id].choices = choices;
         }

         const updatedSurveyData = updateQuestionChoices(state.surveyData, id, choices);
         return { editData: updatedEditData, surveyData: updatedSurveyData };
      });
   },
   setMaxChoices: (sectionId, id, maxChoices) => {
      set((state) => {
         const updatedEditData = { ...state.editData };
         if (!updatedEditData[id]) {
            updatedEditData[id] = {
               maxChoices: maxChoices
            };
         } else {
            updatedEditData[id].maxChoices = maxChoices;
         }

         const updatedSurveyData = updateMaxChoices(state.surveyData, id, maxChoices);
         return { editData: updatedEditData, surveyData: updatedSurveyData };
      });
   },
   setSecondQuestionChoices: (sectionId, id, choices) => {
      set((state) => {
         const updatedEditData = { ...state.editData };
         if (!updatedEditData[id]) {
            updatedEditData[id] = {
               secondChoices: choices
            };
         } else {
            updatedEditData[id].secondChoices = choices;
         }

         const updatedSurveyData = updateSecondQuestionChoices(state.surveyData, id, choices);
         return { editData: updatedEditData, surveyData: updatedSurveyData };
      });
   },
   setRangeOptions: (sectionId, id, rangeOptions) => {
      set((state) => {
         const updatedEditData = { ...state.editData };

         if (!updatedEditData[id]) {
            updatedEditData[id] = {
               range: rangeOptions
            };
         } else {
            updatedEditData[id].range = rangeOptions;
         }

         const updatedSurveyData = updateRangeValues(state.surveyData, id, rangeOptions);
         return { editData: updatedEditData, surveyData: updatedSurveyData };
      });
   }
}));

export const surveyLanguageStore = create((set) => ({
   language: "",
   setLanguage: (value) => set({ language: value })
}));

export const useTranslateStore = create((set) => {
   const initialState = {
      toLanguage: "",
      languageStatus: "",
      labelData: [],
      choiceData: [],
      translationSections: [],
      translationLabels: [],
      translateHints: []
   };

   return {
      ...initialState,
      setToLanguage: (value) => set({ toLanguage: value }),
      setLanguageStatus: (value) => set({ languageStatus: value }),
      handleTextChange: debounce((text, questionId, dataType, description) => {
         set((state) => {
            const existingEntryIndex = state.labelData.findIndex(
               (entry) => entry.questionId === questionId
            );
            let newData;
            if (existingEntryIndex !== -1) {
               const updatedData = [...state.labelData];
               updatedData[existingEntryIndex] = { questionId, text, dataType };
               newData = {};
            } else {
               switch (dataType) {
                  case "Section":
                     const filteredSections = (state.translationSections || []).filter(
                        (entry) => entry.sectionId !== questionId
                     );
                     newData = {
                        ...state,
                        translationSections: [
                           ...filteredSections,
                           { sectionId: questionId, name: text, description: text }
                        ]
                     };
                     break;
                  case "Question":
                     const filteredLabels = (state.translationLabels || []).filter(
                        (entry) => entry.questionId !== questionId
                     );
                     newData = {
                        ...state,
                        translationLabels: [...filteredLabels, { questionId, text }]
                     };
                     break;
                  case "Hints":
                     const filteredHints = (state.translateHints || []).filter(
                        (entry) => entry.questionId !== questionId
                     );
                     newData = {
                        ...state,
                        translateHints: [...filteredHints, { questionId, text }]
                     };
                     break;
                  default:
                     newData = { ...state };
               }

               delete newData.labelData;
            }

            return newData;
         });
      }, 500),
      handleChoiceLabel: debounce((choiceId, value) => {
         set((state) => {
            const existingEntryIndex = state.choiceData.findIndex(
               (entry) => entry.choiceLabels[0].choiceId === choiceId
            );

            if (existingEntryIndex !== -1) {
               const updatedData = [...state.choiceData];
               updatedData[existingEntryIndex].choiceLabels[0].text = value;
               return { choiceData: updatedData };
            } else {
               return {
                  choiceData: [
                     ...state.choiceData,
                     {
                        choiceLabels: [
                           {
                              text: value,
                              choiceId: choiceId
                           }
                        ]
                     }
                  ]
               };
            }
         });
      }, 500),
      initializeTranslation: (inputData) => {
         const initialChoiceData = [];
         const initialTranslationSections = [];
         const initialTranslationLabels = [];

         if (!inputData) {
            set({
               choiceData: [],
               translationSections: [],
               translationLabels: []
            });
            return;
         }

         const initializeItem = (items) => {
            items.forEach((item) => {
               if (item.itemLabel === "Question") {
                  item.surveyItem.questionLabels.forEach((label) => {
                     initialTranslationLabels.push({
                        questionId: label.questionId,
                        text: label.text
                     });
                  });

                  if (item.surveyItem.selectQuestionChoices.length > 0) {
                     item.surveyItem.selectQuestionChoices.forEach((choice) => {
                        choice.choiceLabels.forEach((choiceLabel) => {
                           initialChoiceData.push({
                              choiceLabels: [
                                 {
                                    text: choiceLabel.text.trim(),
                                    choiceId: choiceLabel.choiceId
                                 }
                              ]
                           });
                        });
                     });
                  }
               }

               if (item.itemLabel === "Section") {
                  initialTranslationSections.push({
                     sectionId: item.id,
                     name: item.surveyItem.description,
                     description: item.surveyItem.description
                  });

                  if (item.innerItems) {
                     initializeItem(item.innerItems);
                  }
               }
            });
         };

         initializeItem(inputData.surveyItems);

         set({
            choiceData: initialChoiceData,
            translationSections: initialTranslationSections,
            translationLabels: initialTranslationLabels
         });
      }
   };
});

export const useGetDraftedSurvey = create((set) => ({
   survey: "",
   setSurvey: (value) => set({ survey: value })
}));

export const useSurveySkipLogicStore = create((set) => ({
   skipLogicCreate: [],

   setSkipLogicCreate: (question, logicalOperator, skipLogics) => {
      set((state) => {
         const sectionId = question?.itemLabel === "Section" && question?.id;
         const questionId = !question?.itemLabel && question?.id;

         let itemExists = false;
         const updatedSkipLogic = state.skipLogicCreate.map((item) => {
            if (
               (item.questionId && questionId && item.questionId === questionId) ||
               (item.sectionId && sectionId && item.sectionId === sectionId)
            ) {
               itemExists = true;
               const existingSkipLogic = item.skipLogics.find(
                  (logic) => logic.index === skipLogics[0].index
               );

               if (skipLogics[0].questionType === 33 && skipLogics[0].value) {
                  //later: logicaloperator for selectmany should be put directly from preview
                  // item.logicalOperator = 1;
                  if (existingSkipLogic && existingSkipLogic.value === "") {
                     item.skipLogics = item.skipLogics.map((logic) =>
                        logic.index === skipLogics[0].index ? { ...logic, ...skipLogics[0] } : logic
                     );
                  } else {
                     item.skipLogics.push({ ...skipLogics[0] });
                  }
               } else {
                  if (existingSkipLogic) {
                     item.skipLogics = item.skipLogics.map((logic) =>
                        logic.index === skipLogics[0].index ? { ...logic, ...skipLogics[0] } : logic
                     );
                  } else {
                     item.skipLogics.push(...skipLogics);
                  }
               }
            }
            return item;
         });

         if (!itemExists) {
            const newSkipLogic = {
               sectionId,
               questionId,
               logicalOperator,
               skipLogics
            };

            updatedSkipLogic.push(newSkipLogic);
         }

         return { skipLogicCreate: updatedSkipLogic };
      });
   },
   deleteSkipLogic: (question, index, value) => {
      set((state) => {
         const updatedSkipLogic = state.skipLogicCreate.map((item) => {
            if (item.questionId === question.id || item.sectionId === question.id) {
               if (item.skipLogics.length > 0) {
                  // later: value is not passed in general delete item from skipLogicCreate state
                  if (value) {
                     item.skipLogics = item.skipLogics.filter((logic) => logic.value !== value);
                  } else {
                     item.skipLogics = item.skipLogics.filter((logic) => logic.index !== index);
                  }

                  if (item.skipLogics.length === 0) {
                     return {
                        ...item,
                        skipLogics: []
                     };
                  }
               }
            }
            return item;
         });

         return { skipLogicCreate: updatedSkipLogic };
      });
   },
   setLogicalOperator: (question, logicalOperator) => {
      set((state) => {
         const sectionId = question?.itemLabel === "Section" && question?.id;
         const questionId = !question?.itemLabel && question?.id;

         const updatedSkipLogic = state.skipLogicCreate.map((item) => {
            if (item.questionId === questionId || item.sectionId === sectionId) {
               item.logicalOperator = logicalOperator;
            }
            return item;
         });

         return { skipLogicCreate: updatedSkipLogic };
      });
   },
   // Seeds one item's existing logic, once. Every SectionItem and QuestionItem calls this from an
   // effect that re-runs whenever the tree is replaced, and this used to append unconditionally:
   // the same item queued two or three times per session, and a re-seed landing after the author
   // edited that item put the OLD expression last in the payload, where it won.
   setSkipLogicCreateFromBackend: (skipLogicData) => {
      set((state) => {
         const itemId = skipLogicData.questionId || skipLogicData.sectionId;
         const alreadyQueued = state.skipLogicCreate.some(
            (entry) => (entry.questionId || entry.sectionId) === itemId
         );

         if (!itemId || alreadyQueued) return state;

         return {
            skipLogicCreate: [
               ...state.skipLogicCreate,
               {
                  sectionId: skipLogicData.sectionId,
                  questionId: skipLogicData.questionId,
                  logicalOperator: skipLogicData.logicalOperator,
                  skipLogics: skipLogicData.skipLogics
               }
            ]
         };
      });
   },
   clearSkipLogicCreate: () => {
      set((state) => {
         return {
            skipLogicCreate: []
         };
      });
   }
}));
